/* eslint-disable react/style-prop-object */
import React, { ReactElement, useEffect, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { isNil } from "ramda";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";

import IconSelector from "components/shared/images/IconSelector";
import AppModal from "components/shared/modal/AppModal";
import RebuildBox from "components/shared/rebuildBox/RebuildBox";
import MailboxProBox from "components/wizard/serverWizardComponents/MailboxProBox";
import ServerTypeBox from "components/wizard/serverWizardComponents/ServerTypeBox";

import { getRebuildOs, getServerProvider, postRebuildServer } from "redux/handlers/serverHandler";

import { getServerDetails } from "redux/selectors/serversSelector";
import { getUserBalance } from "redux/selectors/userSelector";

import { useAppDispatch } from "hooks/reduxHook";

import { IOsDetails } from "types/api/serversApiInterface";

const ServerRebuild = (): ReactElement => {
 const intl = useIntl();
 const theme = useTheme();
 const dispatch = useAppDispatch();
 const navigate = useNavigate();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("md"));

 const userBalance = useSelector(getUserBalance);
 const { id } = useSelector(getServerDetails);
 const { locked } = useSelector(getServerDetails);
 const { provider } = useSelector(getServerDetails);

 const [osList, setOsList] = useState<Array<IOsDetails>>([]);
 const [osSelected, setOsSelected] = useState<number>(0);
 const [openModal, setOpenModal] = useState<boolean>(false);
 const [loading, setLoading] = useState<boolean>(false);
 const [selectedType, setSelectedType] = useState<string>("");
 const [loadingOs, setLoadingOs] = useState<boolean>(false);
 const [mailPro, setMailPro] = useState<number>(5);

 const handleOpen = () => setOpenModal(true);
 const handleClose = () => setOpenModal(false);

 const handleConfirm = async () => {
  setLoading(true);
  if (selectedType === "container-mail-pro")
   await dispatch(postRebuildServer(osSelected, id || 0, selectedType, mailPro));
  else await dispatch(postRebuildServer(osSelected, id || 0, selectedType));
  setLoading(false);
  setOpenModal(false);
  navigate("/servers");
 };

 useEffect(() => {
  (async () => {
   setLoadingOs(true);
   const providers = (await dispatch(getServerProvider())).find(
    (element) => element.name === provider
   );
   const data = await dispatch(getRebuildOs(providers?.id || 1));
   setOsList(isNil(data) ? [] : data);
   setLoadingOs(false);
  })();
 }, []);

 const handleSelectOs = (os: IOsDetails) => {
  if (!os.name.includes("debian-12")) {
   setSelectedType("custom");
  }
  setOsSelected(os.id);
 };

 const handleSelectType = (type: string) => {
  if (type !== "custom") {
   setOsSelected(osList.find((element) => element.name === "debian-12")?.id || 0);
  }
  setSelectedType(type);
 };

 return (
  <>
   <Paper elevation={1} sx={{ borderRadius: "5px" }}>
    <Stack p={2} spacing={2}>
     <Stack direction="row" spacing={1}>
      <IconSelector icon="RebuildIcon" props={{ fontSize: "medium", color: "kxOrange" }} />
      <Typography fontSize={16} ml={1} variant="unboundedTitle">
       <FormattedMessage id="server.rebuild" />
      </Typography>
     </Stack>
     <Divider />
     <Alert severity="error" variant="outlined">
      <Typography variant="caption">
       <FormattedMessage
        id={locked ? "server.rebuild.rebuildNotAvailable" : "server.rebuild.warningMessage"}
       />
      </Typography>
     </Alert>
     {!locked &&
      (loadingOs ? (
       <Stack direction="row" alignItems="center" spacing={2}>
        <CircularProgress />
        <Typography variant="subtitle2">
         <FormattedMessage id="server.rebuild.loadingOs" />
        </Typography>
       </Stack>
      ) : (
       <Stack spacing={2}>
        <Typography textAlign="left" sx={{ pl: 1 }} fontWeight="bold">
         <FormattedMessage id="server.rebuild.selectOsForRebuild" />
        </Typography>
        <Grid container spacing={2}>
         {osList.map((element, index) => {
          return (
           <Grid xs={desktopViewPort ? "auto" : 12} key={`os-rebuild-${index}`}>
            <RebuildBox
             os={element}
             selectOs={(os) => handleSelectOs(os)}
             selected={osSelected === element.id}
            />
           </Grid>
          );
         })}
        </Grid>
        <Stack spacing={2}>
         <Typography textAlign="left" sx={{ pl: 1 }} fontWeight="bold">
          <FormattedMessage id="server.rebuild.selectTypeForRebuild" />
         </Typography>
         <ServerTypeBox
          selectedType={selectedType}
          selectType={(type) => handleSelectType(type)}
          showNoDeprecated={true}
         />
        </Stack>
        {selectedType === "container-mail-pro" && (
         <Stack spacing={2} textAlign="left" mx={1}>
          <Typography textAlign="left" fontWeight="bold">
           <FormattedMessage id="server.rebuild.selectProForRebuild" />
          </Typography>
          <MailboxProBox selectedNumber={mailPro} selectNumber={(value) => setMailPro(value)} />
         </Stack>
        )}
        <Stack direction="row" alignItems="center" justifyContent="flex-end">
         <Button
          disabled={
           osSelected === 0 ||
           selectedType === "" ||
           (selectedType === "container-mail-pro" && mailPro < 5)
          }
          onClick={handleOpen}>
          <FormattedMessage id="server.rebuild.confirmRebuild" />
         </Button>
        </Stack>
        <AppModal
         open={openModal}
         close={handleClose}
         title={intl.formatMessage({ id: "server.rebuild.modalTitle" })}
         handleClose={handleClose}
         handleConfirm={handleConfirm}
         disabled={
          loading ||
          osSelected === 0 ||
          locked ||
          (selectedType === "container-mail-pro" && mailPro < 5) ||
          (selectedType === "container-mail-pro" && userBalance < 1.8 * mailPro)
         }>
         <Stack>
          {locked ? (
           <Alert severity="error">
            <FormattedMessage id="server.rebuild.protectionActiveError" />
           </Alert>
          ) : (
           <Stack>
            {selectedType === "container-mail-pro" && (
             <>
              <Typography>
               <FormattedMessage id="server.rebuild.proMessage" />
              </Typography>
              <Stack direction="row" spacing={2} mt={1}>
               <Typography minWidth={150} textAlign="start" fontWeight="bold">
                <FormattedMessage id="server.add.balance" />
               </Typography>
               <Typography>
                <FormattedNumber value={userBalance} style={"currency"} currency="EUR" />
               </Typography>
              </Stack>
              <Stack direction="row" spacing={2}>
               <Typography minWidth={150} textAlign="start" fontWeight="bold">
                <FormattedMessage id="container.maildomains.proPriceTotal" />
               </Typography>
               <Typography>
                <FormattedNumber value={1.8 * mailPro} style={"currency"} currency="EUR" />
               </Typography>
              </Stack>
             </>
            )}
            <Typography>
             <FormattedMessage id="server.rebuild.modalMessage" />
            </Typography>
           </Stack>
          )}
         </Stack>
        </AppModal>
       </Stack>
      ))}
    </Stack>
   </Paper>
  </>
 );
};

export default ServerRebuild;
